import { DndContext, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsSixVertical } from "@phosphor-icons/react";

/* SortableList — thin wrapper around @dnd-kit that lets a caller render any
 * list-of-things with a left-hand drag handle. The caller owns rendering the
 * row body (via `renderItem`) so we don't force a layout on ingredients vs
 * instructions — they look completely different but reorder identically.
 *
 * Notes on mobile: TouchSensor uses `delay: 180` + `tolerance: 8`. That means
 * a plain tap or scroll won't hijack — the user has to press-and-hold on the
 * grip for a beat before the row starts to move. Desktop uses a distance
 * activation (5px) so the mouse never accidentally reorders when clicking an
 * input near the handle.
 */
export default function SortableList({ items, getId, onReorder, renderItem, testidPrefix }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor,   { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = items.map((it, i) => String(getId ? getId(it, i) : i));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onReorder(arrayMove(items, from, to));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {items.map((item, i) => (
          <SortableRow
            key={ids[i]}
            id={ids[i]}
            testid={testidPrefix ? `${testidPrefix}-${i}` : undefined}
          >
            {renderItem(item, i)}
          </SortableRow>
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({ id, children, testid }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    zIndex: isDragging ? 20 : "auto",
    position: "relative",
  };
  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-2" data-testid={testid}>
      {/* Grip handle — long-press on mobile, drag on desktop. touchAction:none
          keeps the browser from stealing the gesture as a scroll while we drag. */}
      <button
        type="button"
        aria-label="Drag to reorder"
        data-testid={testid ? `${testid}-handle` : undefined}
        {...attributes}
        {...listeners}
        style={{ touchAction: "none" }}
        className="mt-2 shrink-0 w-6 h-9 flex items-center justify-center text-rf-ink-soft hover:text-rf-ink cursor-grab active:cursor-grabbing rounded"
      >
        <DotsSixVertical size={18} weight="bold" />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}
